import type { FC } from "react";
import { useCallback } from "react";

import type { AddressFormProviderProps } from "@ui/components/AddressFormProvider";
import { Controller, useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs";
import type { PlaceInputResults } from "@ui/components/PlaceInput";
import { PlaceInput } from "@ui/components/PlaceInput";
import { useTranslations } from "@ui/providers/useTranslations";

import styles from "./JordanForm.module.scss";

type JordanFormProps = Pick<AddressFormProviderProps, "fieldName" | "locationServiceAutocompleteProvider">;

const fieldNameDefault = (name: string) => name;

export const JordanForm: FC<JordanFormProps> = ({
  fieldName = fieldNameDefault,
  locationServiceAutocompleteProvider,
}) => {
  const { __ } = useTranslations();
  const { setValue, trigger, control } = useFormContext();

  const handleAutocompleteChange = useCallback(
    (place: PlaceInputResults) => {
      setValue(fieldName("city"), place.city);
      setValue(fieldName("streetName"), place.streetName);
      setValue(fieldName("streetNumber"), place.streetNumber);
      trigger();
    },
    [fieldName, setValue, trigger]
  );

  return (
    <div className={styles.wrapper}>
      <Controller
        control={control}
        name={fieldName("streetName")}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field, fieldState: { error } }) => (
          <PlaceInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            required
            label={__("address_fields.street_name")}
            placeholder={__("address_fields.street_name_placeholder")}
            onPlace={handleAutocompleteChange}
            autocompleteProvider={locationServiceAutocompleteProvider}
            autoComplete="address-line1"
          />
        )}
      />
      <Controller
        name={fieldName("streetNumber")}
        control={control}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            required
            data-testid="streetNumberJordan"
            label={__("address_fields.street_number")}
            placeholder={__("address_fields.street_number_placeholder")}
            autoComplete="address-line2"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("streetAdditional")}
        render={({ field }) => (
          <TextInput
            {...field}
            data-testid="streetAdditionalJordan"
            label={__("address_fields.street_additional")}
            placeholder={__("address_fields.street_additional_placeholder")}
            autoComplete="address-line3"
          />
        )}
      />
      <Controller
        control={control}
        name={fieldName("city")}
        rules={{ required: __("form_validation.field_required") }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            isError={!!error}
            errorText={error?.message}
            required
            data-testid="cityJordan"
            label={__("address_fields.city")}
            placeholder={__("address_fields.city_placeholder")}
            autoComplete="address-level2"
          />
        )}
      />
    </div>
  );
};
